import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, XCircle, Home, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const linkId = searchParams.get("link_id");
  const [status, setStatus] = useState("loading"); // loading, paid, pending, failed
  const [paymentData, setPaymentData] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus("failed");
    }
  }, [sessionId, retryCount]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === "paid") {
        setStatus("paid");
        setPaymentData({
          amount: data.amount,
          currency: data.currency?.toUpperCase() || "EUR"
        });
      } else if (data.status === "open" || data.payment_status === "unpaid") {
        // Still processing - retry a few times
        if (retryCount < 5) {
          setTimeout(() => setRetryCount(prev => prev + 1), 2000);
        } else {
          setStatus("pending");
        }
      } else {
        setStatus("failed");
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      // On error, retry a few times before failing
      if (retryCount < 3) {
        setTimeout(() => setRetryCount(prev => prev + 1), 2000);
      } else {
        setStatus("failed");
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-2xl shadow-xl border border-[#E7E5E4] overflow-hidden">
          {/* Status Icon Header */}
          <div className={`p-8 text-center ${
            status === "paid" ? "bg-green-50" :
            status === "pending" ? "bg-yellow-50" :
            status === "failed" ? "bg-red-50" :
            "bg-[#F9F8F6]"
          }`}>
            {status === "loading" && (
              <Loader2 size={80} className="mx-auto text-[#D4AF37] animate-spin" />
            )}
            {status === "paid" && (
              <CheckCircle size={80} className="mx-auto text-green-500" />
            )}
            {status === "pending" && (
              <AlertTriangle size={80} className="mx-auto text-yellow-500" />
            )}
            {status === "failed" && (
              <XCircle size={80} className="mx-auto text-red-500" />
            )}
          </div>

          {/* Content */}
          <div className="p-8 text-center">
            {status === "loading" && (
              <>
                <h1 className="font-heading text-2xl text-[#1C1917] mb-2">
                  Verifica del pagamento in corso...
                </h1>
                <p className="text-[#78716C] text-sm">
                  Attendere prego, stiamo verificando il pagamento.
                </p>
              </>
            )}

            {status === "paid" && (
              <>
                <h1 className="font-heading text-3xl text-[#1C1917] mb-4">
                  Pagamento Completato!
                </h1>
                <p className="text-[#78716C] mb-6">
                  Grazie! Il pagamento è stato ricevuto con successo. 
                  Una email di conferma è stata inviata al tuo indirizzo.
                </p>

                {paymentData && (
                  <div className="bg-[#F9F8F6] rounded-lg p-6 text-left mb-6">
                    <h3 className="font-heading text-lg text-[#1C1917] mb-4 text-center">
                      Dettagli Pagamento
                    </h3>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between items-center border-t border-[#E7E5E4] pt-4">
                        <span className="font-heading text-xl font-bold">Totale Pagato</span>
                        <span className="font-heading text-xl font-bold text-[#D4AF37]">
                          € {paymentData.amount?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <Link 
                  to="/" 
                  className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8962E] text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                  data-testid="back-to-home-btn"
                >
                  <Home size={18} />
                  Torna alla Home
                </Link>
              </>
            )}

            {status === "pending" && (
              <>
                <h1 className="font-heading text-2xl text-[#1C1917] mb-4">
                  Pagamento in Elaborazione
                </h1>
                <p className="text-[#78716C] mb-6">
                  Il pagamento è in fase di elaborazione. 
                  Riceverai una email di conferma al completamento.
                </p>
                <Link 
                  to="/" 
                  className="inline-flex items-center gap-2 bg-[#1C1917] hover:bg-[#292524] text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                  data-testid="back-to-home-btn"
                >
                  <Home size={18} />
                  Torna alla Home
                </Link>
              </>
            )}

            {status === "failed" && (
              <>
                <h1 className="font-heading text-2xl text-[#1C1917] mb-4">
                  Errore di Verifica
                </h1>
                <p className="text-[#78716C] mb-6">
                  Non è stato possibile verificare il pagamento. 
                  Se hai completato il pagamento, riceverai comunque una email di conferma.
                </p>
                <Link 
                  to="/" 
                  className="inline-flex items-center gap-2 bg-[#1C1917] hover:bg-[#292524] text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                  data-testid="back-to-home-btn"
                >
                  <Home size={18} />
                  Torna alla Home
                </Link>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-[#F9F8F6] px-8 py-4 text-center">
            <p className="text-sm text-[#78716C]">Suite Lucina · Rome</p>
          </div>
        </div>
      </div>
    </div>
  );
}
